/**
 * CLE-3043 — which estimate on the Estimates list the user has "in focus", for Clear Health AI.
 * A prod chat on a hospital's Estimates list asked "whats this patients payer" and was asked for
 * the patient's name or estimate ID: nothing told the chat which row the user was looking at. The
 * table publishes this id (StaffChatPageHintsContext `focusedOrderId`) and the staff widget sends
 * it as the page's estimate, marked as an expanded row (`orderIdSource: "expandedRow"`).
 *
 * OrdersTable keeps ONE expansion state: the expanded order ids in the order they were opened.
 * The DataTable's `expandedRows` map and the focused id are both derived from it, so they cannot
 * disagree.
 */

/** Expanded order ids, oldest first: the last one is the row opened most recently. */
export type ExpandedOrderIds = readonly string[];

/** The DataTable's `expandedRows` (keyed by `dataKey`, i.e. order id). */
export type ExpandedRows = Record<string, boolean>;

/**
 * Opening a row moves it to the end (it is now the newest); closing one removes it. Opening a row
 * that is already open re-marks it as the newest; closing one that is not open changes nothing.
 */
export const toggleExpandedOrderIds = (
  expanded: ExpandedOrderIds,
  orderId: string,
  open: boolean
): string[] => {
  const without = expanded.filter((id) => id !== orderId);
  return open ? [...without, orderId] : without;
};

/** The DataTable's `expandedRows` map for the expanded ids. */
export const toExpandedRows = (expanded: ExpandedOrderIds): ExpandedRows =>
  Object.fromEntries(expanded.map((orderId) => [orderId, true]));

/**
 * The order id in focus: the most recently opened row that is still expanded AND still in the
 * list. Nothing else — none when nothing listed is expanded (the page / filter moved every
 * expanded row out of the list, or nothing is open).
 */
export const pickFocusedOrderId = (
  expanded: ExpandedOrderIds,
  listedOrderIds: readonly string[]
): string | null => {
  const listed = new Set(listedOrderIds);
  for (let i = expanded.length - 1; i >= 0; i -= 1) {
    if (listed.has(expanded[i])) return expanded[i];
  }
  return null;
};

/**
 * The row a DataTable `onRowToggle` changed: the id added to (opened) or dropped from (closed)
 * the expanded map. `null` when nothing changed.
 */
export const toggledOrderId = (
  previous: ExpandedRows,
  next: ExpandedRows
): { orderId: string; open: boolean } | null => {
  const opened = Object.keys(next).find(
    (orderId) => next[orderId] && !previous[orderId]
  );
  if (opened) return { orderId: opened, open: true };
  const closed = Object.keys(previous).find(
    (orderId) => previous[orderId] && !next[orderId]
  );
  return closed ? { orderId: closed, open: false } : null;
};
